import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import axios from 'axios';
import { FiLoader, FiX } from 'react-icons/fi';
import AlertMessage from '../../common/AlertMessage';

interface AddAnswerProps {
  answerId?: number; // Id della risposta da modificare
  setActiveView: React.Dispatch<React.SetStateAction<'list' | 'add' | 'edit'>>; // Funzione per cambiare vista
}

// Definire un tipo per i dati del modulo
interface AnswerFormData {
  testo: string;
  question_id: string;
  function?: string; // "function" è opzionale
}

const AddAnswer: React.FC<AddAnswerProps> = ({ answerId, setActiveView }) => {
  const { register, handleSubmit, reset } = useForm<AnswerFormData>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [questions, setQuestions] = useState<any[]>([]); // Lista delle domande
  const [selectedQuestion, setSelectedQuestion] = useState<string>(''); // Domanda selezionata

  // Effetto per caricare i dati esistenti se siamo in modalità modifica
  useEffect(() => {
    const fetchAnswerData = async () => {
      if (answerId) {
        try {
          const response = await axios.get(`${process.env.REACT_APP_API_URL}/answers/${answerId}`);
          const data = response.data;
         
          // Riempire il modulo con i dati della risposta
          reset({
            testo: data.answer.testo,
            question_id: data.question ? data.question.id.toString() : '', // Verifica se la domanda esiste
            function: data.function || '',
          });

          // Definire la domanda selezionata
          if (data.question && data.question.id) {
            setSelectedQuestion(data.question.id.toString()); // Mantieni la domanda selezionata
          } else {
            setSelectedQuestion(''); // Nessuna domanda associata
          }
        } catch (error) {
          console.error('Errore durante il caricamento dei dati', error);
          setMessage({ type: 'error', text: 'Errore durante il caricamento dei dati' });
        }
      }
    };

    if (answerId) {
      fetchAnswerData();
    }
  }, [answerId, reset]);

  // Caricare la lista delle domande
  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/questions`);
        setQuestions(response.data);
      } catch (error) {
        console.error('Errore durante il caricamento delle domande', error);
      }
    };

    fetchQuestions();
  }, []);

  const onSubmit = async (data: AnswerFormData) => {
    const answerData: any = {
      testo: data.testo,
      question_id: data.question_id, // Domanda selezionata
    };

    // Se una funzione è presente nel modulo, la aggiungiamo ai dati
    if (data.function) {
      answerData['function'] = data.function;
    }

    try {
      setIsSubmitting(true);

      // Se l'ID della risposta è presente (modifica), aggiorniamo la risposta
      if (answerId) {
        // Aggiorna la risposta
        await axios.put(`${process.env.REACT_APP_API_URL}/answers/${answerId}`, answerData);

        setMessage({
          type: 'success',
          text: `Risposta e domanda associata modificate con successo!`,
        });
      } else {
        // Se l'ID della risposta non è presente (aggiunta), creiamo una nuova risposta
        await axios.post(`${process.env.REACT_APP_API_URL}/answers`, answerData);

        setMessage({
          type: 'success',
          text: `Risposta aggiunta con successo!`,
        });
      }
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || error.message || 'Errore durante il salvataggio',
      });
    } finally {
      setIsSubmitting(false);
    }

    // Torna alla vista lista dopo un ritardo
    setTimeout(() => {
      setActiveView('list');
    }, 2000);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200/80 transition-all duration-300 hover:shadow-md">
      <div className="px-5 py-4 border-b bg-gradient-to-r from-gray-50 to-gray-100/50">
        <h2 className="font-semibold text-gray-700">{answerId ? 'Modifica una risposta' : 'Aggiungi una risposta'}</h2>
      </div>

      <div className="p-5">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Messaggio di avviso */}
          {message && (
            <div
              className={`text-sm p-3 rounded ${message.type === 'error' ? 'bg-red-500 text-white' : ' bg-green-100 text-green-700'} flex justify-between items-center`}
            >
              {message.text}
              <button onClick={() => setMessage(null)} className="ml-2">
                <FiX className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Selezione della domanda */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Domanda</label>
            <select
              {...register('question_id', { required: true })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
              value={selectedQuestion}
              onChange={(e) => setSelectedQuestion(e.target.value)}
            >
              <option value="">Seleziona una domanda</option>
              {questions.map((question) => (
                <option key={question.id} value={question.id}>
                  {question.text}
                </option>
              ))}
            </select>
          </div>

          {/* Visualizzazione del testo della domanda selezionata */}
          {selectedQuestion && questions.length > 0 && (
            <div className="mt-2 text-sm text-gray-600">
              {questions.find(q => q.id === selectedQuestion)?.text}
            </div>
          )}

          {/* Testo della risposta */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Risposta</label>
            <input
              {...register('testo', { required: true })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
              required
            />
          </div>

          {/* Funzione associata alla risposta */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Funzione</label>
            <input
              {...register('function')}
              
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
            />
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-gradient-to-r from-indigo-500 to-purple-500 text-white px-4 py-2 rounded-lg text-sm
              hover:from-indigo-600 hover:to-purple-600 transition-all flex items-center justify-center gap-2
              disabled:opacity-70 disabled:cursor-not-allowed shadow-sm hover:shadow"
          >
            {isSubmitting && <FiLoader className="w-4 h-4 animate-spin" />}
            {isSubmitting ? 'Salvataggio...' : answerId ? 'Modifica' : 'Aggiungi'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AddAnswer;